'use client'

import { Check, LoaderCircleIcon } from 'lucide-react'

import Icon from '@/shared/components/atoms/Icon'
import {
	Stepper,
	StepperItem,
	StepperTrigger,
	StepperIndicator,
	StepperSeparator,
	StepperTitle,
	StepperNav,
} from '@/shared/components/organisms/Stepper'

export type SmartSlotStepperType = 'weather' | 'occupation'

interface SmartSlotStepperProps {
	type: SmartSlotStepperType
	activeStep: number
	onStepChange: (step: number) => void
	completedSteps?: number[]
	canChangeStep?: (nextStep: number) => boolean
}

export const SmartSlotStepper = ({
	type,
	activeStep,
	onStepChange,
	completedSteps = [],
	canChangeStep,
}: SmartSlotStepperProps) => {
	const weatherSteps = [
		{ id: 'cloudy', label: 'Cloudy', icon: 'cloud' },
		{ id: 'sunny', label: 'Sunny', icon: 'sun' },
		{ id: 'rainy', label: 'Rainy', icon: 'rain' },
		{ id: 'snowy', label: 'Snowy', icon: 'snow' },
	]

	const occupancySteps = [
		{ id: 'low', label: 'Low', icon: 'traffic-low' },
		{ id: 'medium', label: 'Medium', icon: 'traffic-mid' },
		{ id: 'high', label: 'High', icon: 'traffic-high' },
	]

	const steps = type === 'weather' ? weatherSteps : occupancySteps

	const handleStepChange = (nextStep: number) => {
		// If canChangeStep is provided, validate before changing
		if (canChangeStep && !canChangeStep(nextStep)) {
			return
		}
		onStepChange(nextStep)
	}

	return (
		<Stepper
			value={activeStep}
			onValueChange={handleStepChange}
			indicators={{
				completed: <Check className="size-4" />,
				loading: <LoaderCircleIcon className="size-4 animate-spin" />,
			}}
			className="space-y-8"
		>
			<StepperNav>
				{steps.map((step, index) => {
					const stepNumber = index + 1
					const isCompleted = completedSteps.includes(stepNumber)

					return (
						<StepperItem
							key={step.id}
							step={stepNumber}
							completed={isCompleted}
							className="relative flex-1 items-start"
						>
							<StepperTrigger
								className="flex flex-col gap-2.5"
								onClick={(e) => {
									e.preventDefault()
									handleStepChange(stepNumber)
								}}
							>
								<StepperIndicator>
									{isCompleted ? (
										<Check className="size-4" />
									) : (
										<Icon name={step.icon} width="16px" height="16px" />
									)}
								</StepperIndicator>
								<StepperTitle>{step.label}</StepperTitle>
							</StepperTrigger>
							{steps.length > stepNumber && (
								<StepperSeparator className="absolute top-3 inset-x-0 left-[calc(50%+0.875rem)] m-0 group-data-[orientation=horizontal]/stepper-nav:w-[calc(100%-2rem+0.225rem)] group-data-[orientation=horizontal]/stepper-nav:flex-none group-data-[state=completed]/step:bg-primary" />
							)}
						</StepperItem>
					)
				})}
			</StepperNav>
		</Stepper>
	)
}
